import {
  select,
  call,
  put,
  fork,
  all,
  getContext,
  takeLeading,
} from 'redux-saga/effects';
import * as actionTypes from 'constants/actionTypes';
import { addTimingMeasureInstructionsToAction } from 'utils/timing';
import { syncFirebasePath } from 'sagas/firebase/internal-lib';
import { selectAccountId, selectAccountMeta } from 'reducers/account/meta';
import { selectAccountUser, selectAccountUsers } from 'reducers/account/users';

import * as firebase from 'sagas/firebase/lib';
import { throttleBy } from 'utils/sagas';
import {
  TRANSFER_ACCEPTED,
  USER_INVITED,
  USER_REMOVED,
  USER_ROLE_UPDATED,
  MAX_ALLOWED_USERS,
} from 'constants/account/users';
import {
  resolveUserRoleUpdate,
  failUserRoleUpdate,
} from 'actions/account/users';
import UserInAccountError from 'errors/UserInAccountError';
import MaxUsersError from 'errors/MaxUsersError';
import { promisaga } from 'utils/as-promised';
import { reportError } from 'helpers/error-reporting';
import { scrapeAcceptInviteToAccountId } from 'utils/url';
import { selectUser, selectUserId } from 'reducers/user';

const createPatternForUserRemoved = userId => action =>
  action &&
  action.type === USER_REMOVED &&
  action.payload &&
  action.payload.id === userId;

export function* replaceAccountUserMeta(userId, data) {
  yield put(
    addTimingMeasureInstructionsToAction(
      {
        type: actionTypes.REPLACE_ACCOUNT_USER_META,
        payload: {
          id: userId,
          meta: data || {},
        },
      },
      actionTypes.SYNC_ACCOUNT_WITH_FIREBASE
    )
  );
}

export function* replaceAccountUser({ key, data }) {
  if (data) {
    yield put({
      type: actionTypes.REPLACE_ACCOUNT_USER,
      payload: {
        id: key,
        ...data,
      },
    });
    yield fork(
      syncFirebasePath,
      `users/${key}/meta`,
      meta => replaceAccountUserMeta(key, meta),
      [actionTypes.CANCEL_ACCOUNT_SYNC, createPatternForUserRemoved(key)]
    );
  }
}

export function* replaceAccountUsers({ data }) {
  yield put({
    type: actionTypes.REPLACE_ACCOUNT_USERS,
    payload: data || {},
  });

  if (data) {
    yield all(
      Object.keys(data).map(userId =>
        fork(
          syncFirebasePath,
          `users/${userId}/meta`,
          meta => replaceAccountUserMeta(userId, meta),
          [actionTypes.CANCEL_ACCOUNT_SYNC, createPatternForUserRemoved(userId)]
        )
      )
    );
  }
}

export function* flushAccountUserToFirebase(action) {
  const { userId } = action.payload;
  const accountId = yield select(selectAccountId);
  const user = yield select(selectAccountUser, userId);

  if (user) {
    const accountUser = {
      ...user,
      meta: null,
      synced: null,
    };
    yield call(
      firebase.set,
      `accounts/${accountId}/users/${userId}`,
      accountUser
    );
  } else {
    yield call(firebase.remove, [`accounts/${accountId}/users/${userId}`]);
  }
}

export function* inviteUser({ payload: { email, name, role } }) {
  try {
    if (!email) {
      throw new ReferenceError('No email specified for user to invite.');
    }

    const accountUsers = yield select(selectAccountUsers);
    const usersAsArr = Object.values(accountUsers || {});
    if (
      usersAsArr
        .map(({ meta }) => meta)
        .some(({ email: userEmail } = {}) => userEmail === email)
    ) {
      throw new UserInAccountError();
    }

    const accountMeta = yield select(selectAccountMeta);
    if (accountMeta.isTrial && usersAsArr.length >= MAX_ALLOWED_USERS) {
      throw new MaxUsersError();
    }

    const accounts = yield getContext('accounts');
    const isExistingUser = yield call(accounts.exists, { email });
    const { email: inviter, id: inviterUid } = yield select(selectUser);

    yield isExistingUser
      ? call(accounts.addUser, {
          email,
          role,
          inviterUid,
        })
      : call(accounts.inviteUser, { email, name, role, inviter });
  } catch (error) {
    if (error.name === 'MaxUsersError' || error.name === 'UserInAccountError') {
      throw error;
    }
    yield call(reportError, error);
    throw error;
  }
}

export function* acceptTransfer({ payload: { accountId } }) {
  try {
    const userId = yield select(selectUserId);
    const accounts = yield getContext('accounts');
    yield call(accounts.acceptTransfer, { accountId, userId });
    yield call(scrapeAcceptInviteToAccountId);
    const auth = yield getContext('auth');
    yield call(auth.logout);
  } catch (error) {
    yield call(reportError, error);
    throw error;
  }
}

export function* updateUserRole({ payload: { userId, role } }) {
  try {
    const accounts = yield getContext('accounts');
    const updatedRawAcctUser = yield call(accounts.updateUserRole, {
      userId,
      role,
    });
    // accountUsers in the store are actually the firebase /account/users/<id>
    // record enriched with the firebase db user object; the api call only gives
    // us the raw firebase account user so we grab and update the existing one
    const user = yield select(selectAccountUser, userId);
    const updatedAcctUser = {
      ...user,
      ...updatedRawAcctUser,
    };

    yield put({
      type: actionTypes.REPLACE_ACCOUNT_USER,
      payload: updatedAcctUser,
    });
    yield put(resolveUserRoleUpdate());
  } catch (error) {
    yield call(reportError, error);
    yield put(failUserRoleUpdate());
    throw error;
  }
}

export default function* users() {
  yield fork(
    throttleBy,
    'userId',
    500,
    actionTypes.FLUSH_ACCOUNT_USER_TO_FIREBASE,
    flushAccountUserToFirebase
  );
  yield takeLeading(USER_INVITED, promisaga(inviteUser));
  yield takeLeading(TRANSFER_ACCEPTED, promisaga(acceptTransfer));
  yield takeLeading(USER_ROLE_UPDATED, promisaga(updateUserRole));
}
